import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea, ScrollBar, ScrollAreaViewport } from "@/components/ui/scroll-area"
import { Progress } from "@/components/ui/progress"
import { 
  AlertCircle, 
  PoundSterling, 
  Clock, 
  CheckCircle2, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Building2,
  FileText,
  Calendar,
  Users,
  Activity,
  BarChart3,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { DashboardSelector } from "@/components/dashboard"
import { UserFilter } from "@/components/filters"
import { useCanViewAllDisputes } from "@/hooks/use-disputes-metrics"
import { useAuth } from "@/hooks/use-auth"
import { useFilter } from "@/contexts/filter-context"
import { useEffect, useMemo, useState } from "react"
import { DataModeBadge } from "@/components/DataModeBadge"
import { getTotalLevelsForDisputeType, getDerivedStatus } from "@/lib/utils/dispute-workflow-utils"
import { useDisputesList } from "@/hooks/useDisputesData"
import { differenceInDays, parseISO, isAfter, isBefore, addDays } from "date-fns"
import { NavigationMetricCardAlt } from "@/components/NavigationMetricCardAlt"

const getPriorityBadgeVariant = (priority: string): { variant?: "default" | "secondary" | "destructive" | "outline" | "warning"; accent?: "teal" | "purple" | "info" | "success" | "warning" | "destructive" | "nile" | "cerulean" | "lagoon" | "eagle" | "sap" | "orange" | "brick" | "red" | "violet" | "blue-violet" } => {
  switch (priority) {
    case "Critical": return { variant: "destructive" }
    case "High": return { variant: "warning", accent: "orange" }
    case "Medium": return { variant: "default" }
    case "Low": return { variant: "secondary" }
    default: return { variant: "default" }
  }
}

const getPriorityBorderColor = (priority: string) => {
  switch (priority) {
    case "Critical": return "rgb(239, 68, 68)"
    case "High": return "rgb(249, 115, 22)"
    case "Medium": return "rgb(148, 163, 184)"
    case "Low": return "rgb(156, 163, 175)"
    default: return "rgb(156, 163, 175)"
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "New": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
    case "In Progress": return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300"
    case "Resolved": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
    case "Closed": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
  }
}

// Helper to get secondary value for active disputes metric card
function getActiveDisputesSecondaryValue(dueSoonCount: number): string {
  if (dueSoonCount > 0) return `${dueSoonCount} due soon`;
  return 'All on track';
}

export default function DisputesDashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canViewAll = useCanViewAllDisputes()
  const { currentFilter, setCurrentFilter } = useFilter()
  
  // Get all disputes from the data hook
  const { data: disputesResult, isLoading } = useDisputesList({ pageSize: 1000 })
  const allDisputes = disputesResult?.disputes ?? [];
  
  // Filter disputes based on current filter (user selection)
  const filteredDisputes = useMemo(() => {
    if (!currentFilter || currentFilter === 'everyone') {
      return allDisputes
    }
    return allDisputes.filter(d => d.assignedTo === currentFilter)
  }, [allDisputes, currentFilter])
  
  // Get the display name for the filter
  const filterUserName = useMemo(() => {
    if (!currentFilter || currentFilter === 'everyone') return 'Team'
    const dispute = allDisputes.find(d => d.assignedTo === currentFilter)
    return dispute?.assignedTo || 'User'
  }, [currentFilter, allDisputes])
  
  // Calculate metrics from actual data
  const metrics = useMemo(() => {
    const activeDisputes = filteredDisputes.filter(d => 
      d.status !== 'Resolved' && d.status !== 'Closed'
    )
    
    const totalAmount = activeDisputes.reduce((sum, d) => sum + d.amount, 0)
    
    // Calculate by priority
    const byPriority = {
      critical: {
        count: activeDisputes.filter(d => d.priority === 'Critical').length,
        amount: activeDisputes.filter(d => d.priority === 'Critical').reduce((sum, d) => sum + d.amount, 0)
      },
      high: {
        count: activeDisputes.filter(d => d.priority === 'High').length,
        amount: activeDisputes.filter(d => d.priority === 'High').reduce((sum, d) => sum + d.amount, 0)
      },
      medium: {
        count: activeDisputes.filter(d => d.priority === 'Medium').length,
        amount: activeDisputes.filter(d => d.priority === 'Medium').reduce((sum, d) => sum + d.amount, 0)
      },
      low: {
        count: activeDisputes.filter(d => d.priority === 'Low').length,
        amount: activeDisputes.filter(d => d.priority === 'Low').reduce((sum, d) => sum + d.amount, 0)
      }
    }
    
    // Calculate by reason/type
    const reasonCounts: Record<string, number> = {}
    activeDisputes.forEach(d => {
      reasonCounts[d.type] = (reasonCounts[d.type] || 0) + 1
    })
    
    const byReason = Object.entries(reasonCounts)
      .map(([reason, count]) => ({
        reason,
        count,
        percentage: Math.round((count / activeDisputes.length) * 100) || 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5) // Top 5
    
    // Calculate average resolution time from resolved disputes
    const resolvedDisputes = filteredDisputes.filter(d => 
      d.status === 'Resolved' || d.status === 'Closed'
    )
    
    let avgResolutionTime = 0
    if (resolvedDisputes.length > 0) {
      const totalDays = resolvedDisputes.reduce((sum, d) => {
        try {
          const created = parseISO(d.createdDate)
          const today = new Date()
          return sum + differenceInDays(today, created)
        } catch {
          return sum
        }
      }, 0)
      avgResolutionTime = Math.round(totalDays / resolvedDisputes.length)
    }
    
    // Calculate resolution rate
    const totalDisputes = filteredDisputes.length
    const resolutionRate = totalDisputes > 0 
      ? Math.round((resolvedDisputes.length / totalDisputes) * 100)
      : 0
    
    // Get overdue disputes
    const today = new Date()
    const overdueDisputes = activeDisputes.filter(d => {
      try {
        const dueDate = parseISO(d.dueDate)
        return isBefore(dueDate, today)
      } catch {
        return false
      }
    })
    
    // Get disputes due soon (within 3 days)
    const dueSoonDisputes = activeDisputes.filter(d => {
      try {
        const dueDate = parseISO(d.dueDate)
        const threeDaysFromNow = addDays(today, 3)
        return isAfter(dueDate, today) && isBefore(dueDate, threeDaysFromNow)
      } catch {
        return false
      }
    })
    
    // Calculate aging buckets
    const agingBuckets = {
      '0-7': 0,
      '8-14': 0,
      '15-30': 0,
      '31+': 0
    }
    
    activeDisputes.forEach(d => {
      try {
        const created = parseISO(d.createdDate)
        const days = differenceInDays(today, created)
        if (days <= 7) agingBuckets['0-7']++
        else if (days <= 14) agingBuckets['8-14']++
        else if (days <= 30) agingBuckets['15-30']++
        else agingBuckets['31+']++
      } catch {
        // Skip invalid dates
      }
    })
    
    // Get recent high-priority disputes
    const recentDisputes = [...activeDisputes]
      .sort((a, b) => {
        // Sort by priority first (Critical > High > Medium > Low)
        const priorityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 }
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 4
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 4
        if (aPriority !== bPriority) return aPriority - bPriority
        
        // Then by created date (newest first)
        try {
          return parseISO(b.createdDate).getTime() - parseISO(a.createdDate).getTime()
        } catch {
          return 0
        }
      })
      .slice(0, 10)
      .map(d => {
        try {
          const created = parseISO(d.createdDate)
          const daysOpen = differenceInDays(today, created)
          return { ...d, daysOpen }
        } catch {
          return { ...d, daysOpen: 0 }
        }
      })
    
    // Calculate workflow progress metrics
    const workflowMetrics = activeDisputes.map(d => ({
      id: d.id,
      progress: d.totalLevels > 0 ? (d.currentLevel / d.totalLevels) * 100 : 0
    }))
    
    const avgWorkflowProgress = workflowMetrics.length > 0
      ? Math.round(workflowMetrics.reduce((sum, m) => sum + m.progress, 0) / workflowMetrics.length)
      : 0
    
    // Assignee workload
    const assigneeWorkload: Record<string, { count: number; amount: number }> = {}
    activeDisputes.forEach(d => {
      if (!assigneeWorkload[d.assignedTo]) {
        assigneeWorkload[d.assignedTo] = { count: 0, amount: 0 }
      }
      assigneeWorkload[d.assignedTo].count++
      assigneeWorkload[d.assignedTo].amount += d.amount
    })
    
    const topAssignees = Object.entries(assigneeWorkload)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
    
    return {
      activeCount: activeDisputes.length,
      totalAmount,
      avgResolutionTime,
      resolutionRate,
      byPriority,
      byReason,
      recentDisputes,
      overdueCount: overdueDisputes.length,
      dueSoonCount: dueSoonDisputes.length,
      agingBuckets,
      avgWorkflowProgress,
      topAssignees,
      isFiltered: currentFilter !== 'everyone' && !!currentFilter,
      userName: filterUserName
    }
  }, [filteredDisputes, currentFilter, filterUserName])
  
  // Log filter synchronization
  useEffect(() => {
    console.log('[DisputesDashboard] Filter synchronized from context:', currentFilter)
  }, [currentFilter])

  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    priorityDisputes: false,
    teamWorkload: false,
    disputesByPriority: false,
    commonTypes: false,
  })

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dispute data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Dashboard Toggle Buttons */}
      <div className="flex items-center justify-between">
        <DashboardSelector />
        
        {/* Filter Dropdown - Only for managers */}
        {canViewAll && (
          <UserFilter 
            value={currentFilter} 
            onValueChange={setCurrentFilter}
          />
        )}
      </div>

      {/* Header Section */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">
              Dispute Dashboard
            {metrics.isFiltered && (
              <span className="text-lg font-normal text-muted-foreground ml-2">
                - {metrics.userName}
              </span>
            )}
            </h1>
            <DataModeBadge />
          </div>
          <p className="text-muted-foreground mt-1">
            {metrics.isFiltered 
              ? `Monitor ${metrics.userName}'s dispute metrics and track resolution progress`
              : 'Monitor team dispute metrics and track resolution progress'
            }
          </p>
        </div>
        <Button 
          onClick={() => navigate("/disputes/all")}
          className="bg-teal-600 hover:bg-teal-700"
        >
          View All Disputes
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Key Metrics Cards Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <NavigationMetricCardAlt
          title="Active Disputes"
          value={metrics.activeCount.toString()}
          secondaryValue={
            metrics.overdueCount > 0 
              ? `${metrics.overdueCount} overdue` 
              : getActiveDisputesSecondaryValue(metrics.dueSoonCount)
          }
          icon={AlertCircle}
          variant="info"
          onClick={() => {
            // Navigate based on what's most urgent
            if (metrics.overdueCount > 0) {
              navigate('/disputes/all?overdue=true');
            } else {
              navigate('/disputes/all');
            }
          }}
        />
        <NavigationMetricCardAlt
          title="Amount in Dispute"
          value={`£${(metrics.totalAmount / 1000).toFixed(1)}K`}
          secondaryValue={`Across ${metrics.activeCount} active dispute${metrics.activeCount !== 1 ? 's' : ''}`}
          icon={PoundSterling}
          variant="info"
          onClick={() => navigate('/disputes/all')}
        />
        <NavigationMetricCardAlt
          title="Avg Resolution Time"
          value={metrics.avgResolutionTime > 0 ? `${metrics.avgResolutionTime} days` : 'N/A'}
          secondaryValue="Based on resolved disputes"
          icon={Clock}
          variant="info"
          onClick={() => navigate('/disputes/all?status=Resolved')}
        />
        <NavigationMetricCardAlt
          title="Resolution Rate"
          value={`${metrics.resolutionRate}%`}
          secondaryValue="Overall success rate"
          icon={CheckCircle2}
          variant="info"
          onClick={() => navigate('/disputes/all?status=Resolved')}
        />
      </div>

      {/* Workflow & Aging Insights Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Workflow Progress */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Workflow Progress</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">{metrics.avgWorkflowProgress}%</span>
                <Badge variant="outline" className="text-xs">
                  {metrics.activeCount} active
                </Badge>
              </div>
              <Progress value={metrics.avgWorkflowProgress} className="h-2" />
              <p className="text-xs text-muted-foreground">
                Average completion across all workflows
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Aging Analysis */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Dispute Aging</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">0-7 days</span>
                <span className="font-medium">{metrics.agingBuckets['0-7']}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">8-14 days</span>
                <span className="font-medium">{metrics.agingBuckets['8-14']}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">15-30 days</span>
                <span className="font-medium">{metrics.agingBuckets['15-30']}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-red-600 font-medium">31+ days</span>
                <span className="font-bold text-red-600">{metrics.agingBuckets['31+']}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Priority Disputes & Team Workload Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Priority Disputes Requiring Attention */}
        <Card>
          <CardHeader className="cursor-pointer" onClick={() => toggleSection('priorityDisputes')}>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold">Priority Disputes Requiring Attention</CardTitle>
                <CardDescription>
                  {metrics.recentDisputes.length > 0 
                    ? `Top ${metrics.recentDisputes.length} highest priority disputes`
                    : 'No active disputes requiring attention'
                  }
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {collapsedSections.priorityDisputes ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          {!collapsedSections.priorityDisputes && (
            <CardContent>
            {metrics.recentDisputes.length > 0 ? (
              <ScrollArea className="h-[600px]">
                <ScrollAreaViewport className="h-full">
                  <div className="space-y-2 pr-4">
                    {metrics.recentDisputes.map((dispute) => (
                    <Card
                      key={dispute.id}
                      className="border-l-4 cursor-pointer hover:shadow-md transition-shadow"
                      style={{
                        borderLeftColor: getPriorityBorderColor(dispute.priority)
                      }}
                      onClick={() => navigate(`/disputes/all/${dispute.id}`)}
                    >
                      <CardContent className="py-3 px-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">{dispute.id}</span>
                            <Badge {...getPriorityBadgeVariant(dispute.priority)} className="text-xs py-0 h-5">
                              {dispute.priority}
                            </Badge>
                            {(() => {
                              const derivedStatus = getDerivedStatus(dispute.currentLevel, getTotalLevelsForDisputeType(dispute.type), dispute.closedReason);
                              return (
                                <Badge className={`${getStatusColor(derivedStatus)} text-xs py-0 h-5`}>
                                  {derivedStatus}
                                </Badge>
                              );
                            })()}
                          </div>
                          <span className="text-base font-bold">
                            £{dispute.amount.toLocaleString()}
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Building2 className="h-3.5 w-3.5" />
                            <span>{dispute.customer}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <FileText className="h-3.5 w-3.5" />
                              <span>{dispute.type}</span>
                            </div>
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="h-3.5 w-3.5" />
                              <span>{dispute.daysOpen} days open</span>
                            </div>
                          </div>
                          {dispute.totalLevels > 0 && (
                            <div className="mt-1.5">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-muted-foreground">Workflow Progress</span>
                                <span className="font-medium">
                                  Level {dispute.currentLevel} of {dispute.totalLevels}
                                </span>
                              </div>
                              <Progress 
                                value={(dispute.currentLevel / dispute.totalLevels) * 100} 
                                className="h-1"
                              />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  </div>
                </ScrollAreaViewport>
                <ScrollBar orientation="vertical" />
              </ScrollArea>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
                <p className="text-lg font-medium">All Clear!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  No disputes requiring immediate attention.
                </p>
              </div>
            )}
            </CardContent>
          )}
        </Card>

        {/* Team Workload Distribution - Only show for managers */}
        {canViewAll && !metrics.isFiltered ? (
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => toggleSection('teamWorkload')}>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold">Team Workload Distribution</CardTitle>
                  <CardDescription>Active disputes by assignee</CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {collapsedSections.teamWorkload ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            {!collapsedSections.teamWorkload && (
              <CardContent>
              {metrics.topAssignees.length > 0 ? (
                <ScrollArea className="h-[600px]">
                  <ScrollAreaViewport className="h-full">
                    <div className="space-y-4 pr-4">
                      {metrics.topAssignees.map((assignee) => (
                      <div key={assignee.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{assignee.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {assignee.count} dispute{assignee.count !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        <span className="text-sm font-medium">
                          £{(assignee.amount / 1000).toFixed(1)}K
                        </span>
                      </div>
                      ))}
                    </div>
                  </ScrollAreaViewport>
                  <ScrollBar orientation="vertical" />
                </ScrollArea>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No Active Assignments</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    No active disputes assigned to team members
                  </p>
                </div>
              )}
              </CardContent>
            )}
          </Card>
        ) : (
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => toggleSection('teamWorkload')}>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold">Workflow Progress</CardTitle>
                  <CardDescription>Average completion across all disputes</CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {collapsedSections.teamWorkload ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            {!collapsedSections.teamWorkload && (
              <CardContent>
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-5xl font-bold text-teal-600 mb-2">
                    {metrics.avgWorkflowProgress}%
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Average workflow completion
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        )}
      </div>

      {/* Disputes by Priority & Common Dispute Types Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Disputes by Priority */}
        <Card>
          <CardHeader className="cursor-pointer" onClick={() => toggleSection('disputesByPriority')}>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold">Disputes by Priority</CardTitle>
                <CardDescription>Current distribution across priority levels</CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {collapsedSections.disputesByPriority ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          {!collapsedSections.disputesByPriority && (
            <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="destructive">Critical</Badge>
                  <span className="text-sm text-muted-foreground">
                    {metrics.byPriority.critical.count} disputes
                  </span>
                </div>
                <span className="text-sm font-medium">
                  £{(metrics.byPriority.critical.amount / 1000).toFixed(1)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="warning" accent="orange">High</Badge>
                  <span className="text-sm text-muted-foreground">
                    {metrics.byPriority.high.count} disputes
                  </span>
                </div>
                <span className="text-sm font-medium">
                  £{(metrics.byPriority.high.amount / 1000).toFixed(1)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="default">Medium</Badge>
                  <span className="text-sm text-muted-foreground">
                    {metrics.byPriority.medium.count} disputes
                  </span>
                </div>
                <span className="text-sm font-medium">
                  £{(metrics.byPriority.medium.amount / 1000).toFixed(1)}K
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary">Low</Badge>
                  <span className="text-sm text-muted-foreground">
                    {metrics.byPriority.low.count} disputes
                  </span>
                </div>
                <span className="text-sm font-medium">
                  £{(metrics.byPriority.low.amount / 1000).toFixed(1)}K
                </span>
              </div>
            </div>
            </CardContent>
          )}
        </Card>

        {/* Common Dispute Types */}
        <Card>
          <CardHeader className="cursor-pointer" onClick={() => toggleSection('commonTypes')}>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold">Common Dispute Types</CardTitle>
                <CardDescription>Top dispute categories</CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {collapsedSections.commonTypes ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          {!collapsedSections.commonTypes && (
            <CardContent>
            {metrics.byReason.length > 0 ? (
              <div className="space-y-4">
                {metrics.byReason.map((item) => (
                  <div key={item.reason} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{item.reason}</span>
                      <span className="text-muted-foreground">{item.count}</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-600 rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No active disputes to categorize
              </div>
            )}
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  )
}
